import _ from 'lodash';
import spacetime from 'spacetime';

import {CREATE_FLOWS, DOCUMENT_TYPES} from '../../../constants';

/**
 * Generate document name based on type and period
 */
export const getDocumentName = (documentType, base, reportPeriod) => {
  switch (documentType) {
    case 'ANNUAL':
      if (_.isNil(base) || _.isEmpty(base)) {
        return `${reportPeriod} - Annual Report`;
      }
      return `${base} - ${reportPeriod} - Annual Report`;
    case 'QUARTER':
      if (_.isNil(base) || _.isEmpty(base)) {
        return `${reportPeriod} - Quarterly Report`;
      }
      return `${base} - ${reportPeriod} - Quarterly Report`;
    case 'SEMI_ANNUAL':
      if (_.isNil(base) || _.isEmpty(base)) {
        return `${reportPeriod} - Semi-Annual Report`;
      }
      return `${base} - ${reportPeriod} - Semi-Annual Report`;
    default:
      return base;
  }
};

/**
 * Calculate report period based on document type and flow
 */
export const calculateReportPeriod = (
  docType,
  fiscalYear,
  quarter = null,
  folderYear = null,
  createFlow = null
) => {
  let year = folderYear !== null ? folderYear : fiscalYear;

  if (createFlow === CREATE_FLOWS.rollforward) {
    if (docType === DOCUMENT_TYPES.annualReport) {
      year = `${Number(year) + 1}`;
    } else if (docType === DOCUMENT_TYPES.quarterlyReport) {
      let currentQuarterNum;
      if (quarter?.toString().includes('Q')) {
        const quarterMatch = quarter.match(/Q(\d)/);
        currentQuarterNum = quarterMatch ? parseInt(quarterMatch[1]) : 1;
      } else {
        currentQuarterNum = parseInt(quarter) || 1;
      }

      if (currentQuarterNum === 4) {
        quarter = 'Q1';
        year = `${Number(year) + 1}`;
      } else {
        quarter = `Q${currentQuarterNum + 1}`;
      }
    } else if (docType === DOCUMENT_TYPES.semiAnnualReport) {
      // Annual -> Semi-Annual: same fiscal year as the next annual would be
      year = `${Number(year) + 1}`;
    }
  }

  if (createFlow === CREATE_FLOWS.pdf) {
    quarter = 'Q1';
  }

  if (
    docType === DOCUMENT_TYPES.annualReport ||
    docType === DOCUMENT_TYPES.semiAnnualReport
  ) {
    return year;
  } else {
    const finalQuarter = quarter || spacetime.now().format('quarter');
    const normalizedQuarter = finalQuarter.match(/Q\d/)
      ? finalQuarter
      : `Q${finalQuarter}`;
    return `${normalizedQuarter} ${year}`;
  }
};

/**
 * Get the effective source type from previous document or prop
 */
export const getEffectiveSourceType = (
  previousDocument,
  sourceDocumentType
) => {
  return previousDocument?.type || sourceDocumentType;
};

/**
 * Check if the create flow is a rollforward type
 */
export const isRollforwardFlow = (createFlow) => {
  return (
    createFlow === CREATE_FLOWS.rollforward || createFlow === CREATE_FLOWS.pdf
  );
};

/**
 * Check if the create flow is an upload type
 */
export const isUploadFlow = (createFlow) => {
  return createFlow === CREATE_FLOWS.upload;
};

/**
 * Determine if document type selection should be restricted
 */
export const shouldRestrictDocumentType = (
  createFlow,
  previousDocument,
  sourceDocumentType
) => {
  return (
    isRollforwardFlow(createFlow) &&
    getEffectiveSourceType(previousDocument, sourceDocumentType)
  );
};

/**
 * Presentation format options
 */
export const presentationOptions = {
  singleYear: {
    label: 'Single-year',
    value: 'SINGLE_YEAR',
  },
  twoYear: {
    label: 'Two-year',
    value: 'TWO_YEAR',
  },
  threeYear: {
    label: 'Three-year',
    value: 'THREE_YEAR',
  },
};

/**
 * Valid presentation format transitions for rollforward.
 * Maps a prior format to the set of allowed destination formats.
 */
const ALLOWED_FORMAT_TRANSITIONS = {
  SINGLE_YEAR: ['SINGLE_YEAR', 'TWO_YEAR'],
  TWO_YEAR: ['SINGLE_YEAR', 'TWO_YEAR', 'THREE_YEAR'],
  THREE_YEAR: ['SINGLE_YEAR', 'TWO_YEAR', 'THREE_YEAR'],
};

/**
 * Returns the set of allowed destination presentation formats given
 * the previous document's format. If no previous format (non-rollforward),
 * all formats are allowed.
 */
export const getAllowedPresentationFormats = (previousFormat) => {
  if (!previousFormat) {
    return Object.values(presentationOptions).map((opt) => opt.value);
  }
  return (
    ALLOWED_FORMAT_TRANSITIONS[previousFormat] ||
    Object.values(presentationOptions).map((opt) => opt.value)
  );
};

/**
 * Valid report type transitions for rollforward.
 */
const ALLOWED_TYPE_TRANSITIONS = {
  [DOCUMENT_TYPES.annualReport]: [
    DOCUMENT_TYPES.annualReport,
    DOCUMENT_TYPES.semiAnnualReport,
  ],
  [DOCUMENT_TYPES.quarterlyReport]: [DOCUMENT_TYPES.quarterlyReport],
  [DOCUMENT_TYPES.semiAnnualReport]: [DOCUMENT_TYPES.annualReport],
};

/**
 * Returns the set of allowed destination report types given the source
 * document type. If no source type (non-rollforward), all types are allowed.
 */
export const getAllowedReportTypes = (sourceType) => {
  if (!sourceType) {
    return [
      DOCUMENT_TYPES.annualReport,
      DOCUMENT_TYPES.quarterlyReport,
      DOCUMENT_TYPES.semiAnnualReport,
    ];
  }
  return (
    ALLOWED_TYPE_TRANSITIONS[sourceType] || [
      DOCUMENT_TYPES.annualReport,
      DOCUMENT_TYPES.quarterlyReport,
      DOCUMENT_TYPES.semiAnnualReport,
    ]
  );
};

/**
 * Report type dropdown options
 */
export const REPORT_TYPE_OPTIONS = [
  {
    label: 'Annual report',
    type: DOCUMENT_TYPES.annualReport,
  },
  {
    label: 'Quarterly report',
    type: DOCUMENT_TYPES.quarterlyReport,
  },
  {
    label: 'Semi-Annual report',
    type: DOCUMENT_TYPES.semiAnnualReport,
  },
];

/**
 * Derive a display-friendly folder name from a document type.
 */
export const getFolderNameForType = (documentType) => {
  switch (documentType) {
    case DOCUMENT_TYPES.annualReport:
      return 'Annual';
    case DOCUMENT_TYPES.quarterlyReport:
      return 'Quarterly';
    case DOCUMENT_TYPES.semiAnnualReport:
      return 'Semi-Annual';
    default:
      return 'Annual';
  }
};

/**
 * Extract the year portion from a report period string.
 * Handles "2025" (Annual/Semi-Annual) and "Q1 2025" (Quarterly) formats.
 */
export const extractYearFromPeriod = (reportPeriod, documentType) => {
  if (documentType === DOCUMENT_TYPES.quarterlyReport) {
    return reportPeriod.split(' ')[1];
  }
  return reportPeriod;
};
